// Catching Chrome's install offer.
//
// `beforeinstallprompt` fires once, early — as soon as the browser has the
// manifest and the service worker and decides the app is installable. That is
// usually before React has mounted anything, so a listener registered inside a
// component's effect arrives too late and the offer is simply lost: no banner,
// no error, nothing to notice.
//
// So the listener is registered here at module scope, which runs while the
// bundle is still evaluating, and the event is held until something asks for
// it. (An inline <script> in index.html would be earlier still, but the CSP is
// `script-src 'self'` and inline scripts are blocked — deliberately.)

/**
 * How long a dismissal lasts.
 *
 * "Never again" was too strong: a device gets re-imaged, a tablet changes
 * hands, or the person who swiped it away was not the one who decides. But a
 * banner that returns quickly is one people learn to dismiss without reading,
 * which costs more than it gains. A month is long enough to stop being noise.
 */
export const INSTALL_REPROMPT_DAYS = 30;

/**
 * Whether the invitation should be shown, given when it was last dismissed.
 *
 * Dates are 'YYYY-MM-DD' keys in the factory's own timezone, so "30 days" means
 * 30 calendar days on the floor rather than a rolling 720 hours that drifts.
 */
export function shouldOfferInstall(dismissedOn: string | null, today: string): boolean {
  if (!dismissedOn) return true;
  const then = Date.parse(`${dismissedOn}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(then) || Number.isNaN(now)) return true;

  const days = (now - then) / 86_400_000;
  // A dismissal dated in the future means the clock was wrong when it was
  // written. Waiting for real time to catch up could hide the banner for
  // months, so treat it as broken and offer. Being asked once too often is
  // recoverable; being silently unable to install is not.
  if (days < 0) return true;
  return days >= INSTALL_REPROMPT_DAYS;
}

/** The non-standard event Chrome fires. Not in lib.dom. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let captured: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

/**
 * iOS, including iPads that lie about being a Mac.
 *
 * `navigator.platform` reports MacIntel on iPadOS, so the touch-point count is
 * what separates an iPad from a desktop Safari — a real Mac reports zero. This
 * is a heuristic and is treated as one: getting it wrong shows somebody a
 * Share-sheet instruction they can ignore, which is the cheap direction to be
 * wrong in.
 *
 * For `lib/pushHealth`, which needs it to tell "your browser cannot do
 * notifications" apart from "this browser cannot do them until the app is on
 * the home screen" — the same sniff, and only one of those is actionable.
 */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Running from the home screen rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone;
  return window.matchMedia?.('(display-mode: standalone)').matches === true || iosStandalone === true;
}

if (typeof window !== 'undefined') {
  installed = isStandalone();

  window.addEventListener('beforeinstallprompt', (e) => {
    // Without this Chrome shows its own mini-infobar, and the app's invitation
    // would be the second thing asking.
    e.preventDefault();
    captured = e as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    captured = null;
    emit();
  });
}

export const installStore = {
  get: () => captured,
  installed: () => installed,
  /** Chrome will not replay the same event, so it is dropped once used. */
  clear: () => {
    captured = null;
    emit();
  },
  subscribe: (cb: () => void) => {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  },
};
